import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import {
  deleteAthleteFromMeet,
  getExportAthleteEvents,
  getExportMeetRegistration,
  getMeetRegistrationByMeetId,
  getMeetRegistrationStatsByMeetId,
} from 'api/meetRegistrationApi';
import {
  generateMeetDetail,
  transformMeetRegistrationData,
} from 'services/admin/MeetService';
import { getMeetComplete } from 'api/meetApi';
import { useNavigate } from 'react-router-dom';
import routes from 'routes';
import { toast } from 'react-toastify';
import AdminTablePageLayout from '../../AdminTablePageLayout';
import MeetPageHeader from 'components/NewDesign/MeetPageHeader';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import { convertDateTime } from 'utils/time';
import styles from './MeetRegistrationEdit.module.css';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';
import { exportAndDownloadFile } from 'utils/fileDownload';
import AthleteEventsEditPopup from '../AddEditRegistrationPopup/AddEditRegistrationPopup';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import ParticipatingClubsSummary from 'components/admin/ParticipatingClubsSummary';
import PrintLabelsModal from 'components/admin/PrintLabelsModal';
import useGroupLabels from 'hooks/useGroupLabels';
import { formatGroupHeading } from 'utils/groupLabel';

const SESSION_STORAGE_TAB_KEY = 'meetRegistration_activeTab';

const MeetRegistrationEdit = ({ clubId, meetId }) => {
  // How the meet's club groups its athletes. A group's label in the filter, the search results, the footer and
  // the print dialog reads "U11 M" for an Under club and "11 M" for an Age club. See utils/groupLabel.js.
  const { groupingBasis } = useGroupLabels(clubId);
  const [meet, setMeet] = useState({});
  const [meetLoading, setMeetLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [registrationLoading, setRegistrationLoading] = useState(false);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('');
  const [selectedClub, setSelectedClub] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const [transformedData, setTransformedData] = useState({
    athletes: {},
    ageGroupTabs: [],
    eventDetails: {},
  });
  const [rawRegistrationData, setRawRegistrationData] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [showAddEditRegistrationPopup, setShowAddEditRegistrationPopup] = useState(false);
  const [popupMode, setPopupMode] = useState('edit'); // 'add' | 'edit'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [athleteToDelete, setAthleteToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showPrintLabels, setShowPrintLabels] = useState(false);
  const navigate = useNavigate();
  const userPermissionLevel = getUserPermissionLevel();
  const isOfficialOrHigher =
    userPermissionLevel >= UserPermissionLevel.Official;
  const isAdminOrHigher = userPermissionLevel >= UserPermissionLevel.Admin;

  // TODO: a logged-in user should be able to edit the registration for THEIR OWN
  // athletes (their athlete's registered events) up to the registration deadline —
  // not only Official+. Today the add/edit form is gated to isOfficialOrHigher, so a
  // member/guest/visitor can't edit their own athlete's events here. What they may
  // edit depends on their role in the club (e.g. an official can edit anyone's; a
  // parent only their own athletes), and is bounded by the registration deadline
  // (deadline field still to be added to meet settings). Server must enforce both
  // the own-athlete scope and the deadline.

  // A meet that has already taken place is a historical record — unregistering would erase the athlete's
  // participation, so the server blocks it and we hide the unregister control here. Same-day is still allowed
  // (strictly-before-today), matching the server guard in DeleteAthleteRegistration.
  const isPastMeet = (() => {
    if (!meet.meetDate) return false;
    const meetDay = new Date(meet.meetDate);
    if (Number.isNaN(meetDay.getTime())) return false;
    meetDay.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return meetDay < today;
  })();

  const handleAgeGroupChange = (_name, value) => {
    setSelectedAgeGroup(value);
    sessionStorage.setItem(`${SESSION_STORAGE_TAB_KEY}_${meetId}`, value);
    // Clear any active search so the newly selected group browses cleanly
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const handleClubChange = (_name, value) => {
    setSelectedClub(value);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setShowSearchResults(true);
  };

  // Selecting a search result jumps to that athlete's age group and narrows
  // the table to them (clearing the search shows the whole group again).
  const handleSelectSearchResult = (match) => {
    setSelectedAgeGroup(match.groupId);
    sessionStorage.setItem(
      `${SESSION_STORAGE_TAB_KEY}_${meetId}`,
      match.groupId
    );
    setSearchQuery(match.name);
    setShowSearchResults(false);
  };

  // Sort age group tabs in correct age order
  const sortAgeGroupTabs = (tabs) => {
    return [...tabs].sort((a, b) => {
      const extractAgeAndGender = (str) => {
        const match = str.toLowerCase().match(/u(\d+)([mf]?)/);
        if (match) {
          const age = parseInt(match[1], 10);
          const gender = match[2] || '';
          return { age, gender };
        }
        return { age: 9999, gender: str };
      };

      const aData = extractAgeAndGender(a.id);
      const bData = extractAgeAndGender(b.id);

      if (aData.age !== bData.age) {
        return aData.age - bData.age;
      }

      if (aData.gender !== bData.gender) {
        if (!aData.gender && bData.gender) return -1;
        if (aData.gender && !bData.gender) return 1;
        if (aData.gender === 'f' && bData.gender === 'm') return -1;
        if (aData.gender === 'm' && bData.gender === 'f') return 1;
      }

      return 0;
    });
  };

  const handleEditAthlete = (athleteId) => {
    const selectedAthlete = rawRegistrationData.find((athlete) => athlete.athleteID === athleteId);
    setSelectedAthlete(selectedAthlete);
    setPopupMode('edit');
    setShowAddEditRegistrationPopup(true);
  };

  const handleAddAthlete = () => {
    setSelectedAthlete(null);
    setPopupMode('add');
    setShowAddEditRegistrationPopup(true);
  };

  const handleDeleteAthlete = (athleteId) => {
    const athlete = rawRegistrationData.find((a) => a.athleteID === athleteId);
    setAthleteToDelete(athlete);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAthlete = async () => {
    if (!athleteToDelete) return;

    setIsDeleting(true);
    try {
      const res = await deleteAthleteFromMeet(meetId, athleteToDelete.athleteID);
      toast.success(
        res?.data?.visitorRemovedFromClub
          ? 'Visitor unregistered and removed from your club'
          : 'Athlete unregistered successfully'
      );
      fetchMeetRegistration();
      fetchMeetAthletesStats();
      setShowDeleteConfirm(false);
      setAthleteToDelete(null);
    } catch (error) {
      console.error('Error deleting athlete registration:', error);
      toast.error(error?.response?.data?.message || 'Failed to unregister athlete');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSaveAthleteEvents = () => {
    // Refresh registration data after save
    fetchMeetRegistration();
    fetchMeetAthletesStats();
  };

  const handleCloseAthleteEventsPopup = (shouldRefresh = false) => {
    setShowAddEditRegistrationPopup(false);
    setSelectedAthlete(null);
    if (shouldRefresh) {
      fetchMeetRegistration();
      fetchMeetAthletesStats();
    }
  };

  const handleImportRegistrationCSV = () => {
    navigate(routes.admin.meetRegistrationImport.url(meetId));
  };

  const fetchMeet = useCallback(async () => {
    try {
      setMeetLoading(true);
      const response = await getMeetComplete(clubId, meetId);
      const { meet: meetData } = response.data;
      setMeet(generateMeetDetail(meetData, true));
    } catch (err) {
      setMeetLoading(false);
      console.log('[ShowMeet] Fetch result error: ', err);
      if (err.response?.status === 404) {
        navigate(routes.notFound);
      } else {
        toast.error(
          err.response?.data?.message || 'Failed to load meet details'
        );
        navigate(routes.admin.dashboard);
      }
    } finally {
      setMeetLoading(false);
    }
  }, [clubId, meetId, navigate]);

  const fetchMeetAthletesStats = useCallback(async () => {
    try {
      const response = await getMeetRegistrationStatsByMeetId(meetId);
      setStatsData(response?.data);
    } catch (err) {
      console.log('[ShowMeet] Fetch meet athletes stats error: ', err);
    }
  }, [meetId]);

  // The club's grouping is listed below because the labels are built here. It is in memory before this page is
  // shown and a club's grouping never changes, so it does not cause a second fetch.
  const fetchMeetRegistration = useCallback(async () => {
    try {
      setRegistrationLoading(true);
      const response = await getMeetRegistrationByMeetId(meetId, true);
      setRawRegistrationData(response?.data);

      const transformed = transformMeetRegistrationData(
        response?.data,
        groupingBasis
      );
      const sortedTabs = sortAgeGroupTabs(transformed.ageGroupTabs);

      setTransformedData({
        ...transformed,
        ageGroupTabs: sortedTabs,
      });

      // Restore saved age group from session storage, or default to the first group
      const savedTab = sessionStorage.getItem(`${SESSION_STORAGE_TAB_KEY}_${meetId}`);
      const savedTabValid = sortedTabs.some((tab) => tab.id === savedTab);
      setSelectedAgeGroup(
        savedTabValid ? savedTab : sortedTabs.length > 0 ? sortedTabs[0].id : ''
      );
    } catch (err) {
      setRegistrationLoading(false);
    } finally {
      setRegistrationLoading(false);
    }
  }, [meetId, groupingBasis]);

  useEffect(() => {
    fetchMeet();
    fetchMeetRegistration();
    fetchMeetAthletesStats();
  }, [fetchMeet, fetchMeetAthletesStats, fetchMeetRegistration]);

  // All athletes across every age group, in sorted age-group order
  const allAthletes = useMemo(() => {
    return transformedData.ageGroupTabs.flatMap(
      (tab) => transformedData.athletes[tab.id] || []
    );
  }, [transformedData.ageGroupTabs, transformedData.athletes]);

  // Athletes for the selected age group (the table always shows a single group
  // so the event columns stay specific to that group)
  const currentAthletes = useMemo(() => {
    return transformedData.athletes[selectedAgeGroup] || [];
  }, [transformedData.athletes, selectedAgeGroup]);

  // Age group filter options (each group with its athlete count)
  const ageGroupFilterOptions = useMemo(() => {
    return transformedData.ageGroupTabs.map((tab) => ({
      value: tab.id,
      label: `${tab.label} (${tab.count})`,
    }));
  }, [transformedData.ageGroupTabs]);

  // Club filter options ("All Clubs" first, then each club). Athlete counts are intentionally omitted here — they're
  // already shown per club (and as a total) in the participating-clubs boxes above, so repeating them is just noise.
  const clubFilterOptions = useMemo(() => {
    const clubs = Array.from(
      new Set(allAthletes.map((athlete) => athlete.club || 'Unknown'))
    )
      .sort((a, b) => a.localeCompare(b))
      .map((club) => ({ value: club, label: club }));
    return [{ value: 'all', label: 'All Clubs' }, ...clubs];
  }, [allAthletes]);

  // Filter athletes by selected club then search query
  const filteredAthletes = useMemo(() => {
    let result = currentAthletes;

    if (selectedClub !== 'all') {
      result = result.filter((athlete) => athlete.club === selectedClub);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (athlete) =>
          athlete.name?.toLowerCase().includes(query) ||
          athlete.club?.toLowerCase().includes(query) ||
          athlete.number?.toString().includes(query)
      );
    }

    return result;
  }, [currentAthletes, selectedClub, searchQuery]);

  // Human-readable label for the active age group filter
  const activeAgeLabel = useMemo(() => {
    return (
      transformedData.ageGroupTabs.find((tab) => tab.id === selectedAgeGroup)
        ?.label || 'Unknown'
    );
  }, [selectedAgeGroup, transformedData.ageGroupTabs]);

  // Typeahead matches across ALL age groups so an athlete can be found even
  // when their group is unknown. Selecting one jumps to that group.
  const searchMatches = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return [];
    return allAthletes
      .filter(
        (athlete) =>
          athlete.name?.toLowerCase().includes(query) ||
          athlete.club?.toLowerCase().includes(query) ||
          athlete.number?.toString().includes(query)
      )
      .slice(0, 8)
      .map((athlete) => ({
        id: athlete.id,
        name: athlete.name,
        club: athlete.club,
        number: athlete.number,
        groupId: `u${athlete.ageGroup}${athlete.gender?.toLowerCase()}`,
        groupLabel: `${formatGroupHeading(groupingBasis, athlete.ageGroup)} ${athlete.gender === 'M' ? 'M' : 'F'}`,
      }));
  }, [allAthletes, searchQuery, groupingBasis]);

  // Close the search results dropdown when clicking outside the search box
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Derive age group options from tabs for the Add mode popup
  const ageGroupOptions = useMemo(() => {
    return transformedData.ageGroupTabs.map(tab => {
      // Parse tab.id like "u12f" or "u14m" or "u12"
      const match = tab.id.toLowerCase().match(/u(\d+)([mf]?)/);
      if (match) {
        return {
          id: tab.id,
          label: tab.label,
          ageGroup: parseInt(match[1], 10),
          gender: match[2]?.toUpperCase() || null
        };
      }
      return null;
    }).filter(Boolean);
  }, [transformedData.ageGroupTabs]);

  // Age-group buckets for the print-labels dialog, in the page's sorted tab
  // order, with athlete rows mapped to the label generator's field names.
  const labelGroups = useMemo(() => {
    return transformedData.ageGroupTabs.map((tab) => ({
      id: tab.id,
      label: tab.label,
      athletes: (transformedData.athletes[tab.id] || []).map((a) => ({
        athleteName: a.name,
        ageGroup: a.ageGroup,
        gender: a.gender,
        clubAthleteNumber: a.number,
      })),
    }));
  }, [transformedData.ageGroupTabs, transformedData.athletes]);

  // Get only events that have been selected by at least one athlete
  const selectedEventCodes = useMemo(() => {
    if (!currentAthletes.length || !transformedData.eventDetails) {
      return [];
    }

    const selectedEvents = new Set();
    currentAthletes.forEach((athlete) => {
      Object.keys(transformedData.eventDetails).forEach((eventCode) => {
        const eventKey = transformedData.eventDetails[eventCode].propertyName;
        if (athlete[eventKey]) {
          selectedEvents.add(eventCode);
        }
      });
    });

    return Array.from(selectedEvents).sort((a, b) => {
      const eventA = transformedData.eventDetails[a];
      const eventB = transformedData.eventDetails[b];
      return (eventA?.rotationOrder || 0) - (eventB?.rotationOrder || 0);
    });
  }, [currentAthletes, transformedData.eventDetails]);

  const handleExportRegistrations = async () => {
    const filename = `${meet.meetName.replace(/\s+/g, '_').toLowerCase()}_exported_registrations.xlsx`;
    await exportAndDownloadFile(
      getExportMeetRegistration,
      [meetId],
      filename,
      'Registration file was successfully exported',
      'Fetching registration data from the server...'
    );
  };

  const handleExportAthleteEvents = async () => {
    const filename = `${meet.meetName.replace(/\s+/g, '_').toLowerCase()}_exported_athlete_events.xlsx`;
    await exportAndDownloadFile(
      getExportAthleteEvents,
      [meetId],
      filename,
      'Athlete events file was successfully exported',
      'Fetching athlete events data from the server...'
    );
  };

  return (
    <AdminTablePageLayout loading={meetLoading || registrationLoading}>
      <MeetPageHeader
        title="Meet Registrations"
        meetName={meet.meetName}
        meetDate={convertDateTime(meet.meetDate).date}
        onBackToMeet={() => navigate(routes.admin.showMeet.url(clubId, meetId))}
      >
        {/* Cross-link to the private self-service view — only meaningful when self-service is enabled. Lets an
            admin/official jump to "My Registrations" to manage their own athletes. */}
        {meet.selfServiceRegistrationEnabled && (
          <CustomNewButton
            variant="success"
            label="My Registrations"
            onClick={() =>
              navigate(routes.admin.myMeetRegistrations.url(clubId, meetId))
            }
          />
        )}
        {isOfficialOrHigher && (
          <>
            <CustomNewButton
              variant="primary2"
              label="Import CSV"
              onClick={handleImportRegistrationCSV}
            />
            <CustomNewButton
              variant="info"
              label="Export Registrations"
              onClick={handleExportRegistrations}
            />
            <CustomNewButton
              variant="info"
              label="Export Events"
              onClick={handleExportAthleteEvents}
            />
            <CustomNewButton
              variant="info"
              label="Print Barcodes"
              onClick={() => setShowPrintLabels(true)}
            />
          </>
        )}
      </MeetPageHeader>

      <div className={styles.container}>
        <div className={styles.clubsSummary}>
          <ParticipatingClubsSummary
            clubs={(statsData?.clubsWithAthleteCount || []).map((club) => ({
              name: club.key,
              count: club.value,
              isHost:
                !!statsData?.hostClubName &&
                club.key?.trim().toLowerCase() ===
                  statsData.hostClubName.trim().toLowerCase(),
            }))}
            totalClubs={statsData?.totalClubs ?? 0}
            totalAthletes={statsData?.totalAthletes ?? 0}
          />
        </div>

        <div className={styles.content}>
          {/* Search bar + age group / club filters */}
          <div className={styles.filtersRow}>
            <div className={styles.searchContainer} ref={searchRef}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search by name, club, or number..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() =>
                  searchQuery.trim() && setShowSearchResults(true)
                }
              />
              {showSearchResults && searchMatches.length > 0 && (
                <div className={styles.searchResults}>
                  {searchMatches.map((match) => (
                    <button
                      key={match.id}
                      type="button"
                      className={styles.searchResultItem}
                      onClick={() => handleSelectSearchResult(match)}
                    >
                      <span className={styles.searchResultName}>
                        {match.name}
                      </span>
                      <span className={styles.searchResultMeta}>
                        {match.club} · {match.groupLabel}
                        {match.number && match.number !== '---'
                          ? ` · #${match.number}`
                          : ''}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.filterDropdown}>
              <CustomDropdown
                name="ageGroup"
                value={selectedAgeGroup}
                options={ageGroupFilterOptions}
                onChange={handleAgeGroupChange}
              />
            </div>
            <div className={styles.filterDropdown}>
              <CustomDropdown
                name="club"
                value={selectedClub}
                options={clubFilterOptions}
                onChange={handleClubChange}
              />
            </div>
            {/* Add Athlete lives here (right of the filters, above the table) so
                the "add a row" action sits next to the data it adds to. */}
            {isOfficialOrHigher && (
              <CustomNewButton
                variant="danger"
                label="+ Add Athlete"
                onClick={handleAddAthlete}
                style={{ marginLeft: 'auto' }}
              />
            )}
          </div>

          {/* Simple custom table */}
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.thLeft}>ATHLETE</th>
                  <th className={styles.thLeft}>CLUB</th>
                  <th className={styles.thLeft}>NUMBER</th>
                  {selectedEventCodes.map((eventCode) => {
                    const eventName =
                      transformedData.eventDetails[eventCode]?.name;
                    // Split event names with spaces or "HURDLES"
                    const lines = eventName.includes('HURDLES')
                      ? eventName.split(' ')
                      : eventName.includes(' ')
                        ? eventName.split(' ')
                        : [eventName];

                    return (
                      <th key={eventCode} className={styles.thCenter}>
                        {lines.map((line, idx) => (
                          <div key={idx}>{line}</div>
                        ))}
                      </th>
                    );
                  })}
                  {isOfficialOrHigher && (
                    <th className={styles.thCenter}>ACTIONS</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredAthletes.length > 0 ? (
                  filteredAthletes.map((athlete) => (
                    <tr key={athlete.id}>
                      <td className={styles.tdLeft}>{athlete.name}</td>
                      <td className={styles.tdLeft}>{athlete.club}</td>
                      <td className={styles.tdLeft}>{athlete.number}</td>
                      {selectedEventCodes.map((eventCode) => {
                        const eventKey =
                          transformedData.eventDetails[eventCode].propertyName;
                        return (
                          <td key={eventCode} className={styles.tdCenter}>
                            {athlete[eventKey] && (
                              <span className={styles.eventDot}>●</span>
                            )}
                          </td>
                        );
                      })}
                      {isOfficialOrHigher && (
                        <td className={styles.tdCenter}>
                          <div className={styles.actionButtons}>
                            <button
                              className={styles.actionBtn}
                              onClick={() =>
                                handleEditAthlete(
                                  athlete.athleteId || athlete.id
                                )
                              }
                              title="Edit"
                            >
                              ✏️
                            </button>
                            {/* Unregister is blocked once the meet has taken place — its registrations are kept as
                                history (server enforces this too). */}
                            {!isPastMeet && (
                              <button
                                className={styles.actionBtn}
                                onClick={() =>
                                  handleDeleteAthlete(
                                    athlete.athleteId || athlete.id
                                  )
                                }
                                title="Unregister from meet"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={
                        3 +
                        selectedEventCodes.length +
                        (isOfficialOrHigher ? 1 : 0)
                      }
                      className={styles.noData}
                    >
                      {searchQuery || selectedClub !== 'all'
                        ? 'No athletes found matching your filters'
                        : 'No athletes registered in this age group'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className={styles.footer}>
          <div className={styles.athleteCount}>
            Total: <strong>{filteredAthletes.length}</strong>
            {filteredAthletes.length === 1 ? ' athlete' : ' athletes'}
            {(searchQuery || selectedClub !== 'all') &&
              ` (filtered from ${currentAthletes.length})`}
            {' in '}
            {activeAgeLabel}
            {selectedClub !== 'all' && ` • ${selectedClub}`}
          </div>
        </div>
      </div>
      {/* Official+ only — this add/edit form must never be presented to a Member or below (defence in depth:
          the openers above are already gated, this guarantees the form is never even mounted). */}
      {isOfficialOrHigher && (
        <AthleteEventsEditPopup
          isOpen={showAddEditRegistrationPopup}
          mode={popupMode}
          athlete={selectedAthlete}
          meetId={meetId}
          clubId={clubId}
          ageGroupOptions={ageGroupOptions}
          isAdmin={isAdminOrHigher}
          onClose={handleCloseAthleteEventsPopup}
          onSave={handleSaveAthleteEvents}
        />
      )}
      {/* Mounted only while open so the form state (group selection) resets each time. */}
      {showPrintLabels && (
        <PrintLabelsModal
          isOpen={showPrintLabels}
          groups={labelGroups}
          onClose={() => setShowPrintLabels(false)}
        />
      )}
      <CustomNewConfirm
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setAthleteToDelete(null);
        }}
        title="Delete Athlete Registration"
        description={`Are you sure you want to unregister ${athleteToDelete?.firstName} ${athleteToDelete?.lastName} from this meet?`}
        confirmButtonText="Delete Registration"
        confirmButtonVariant="danger"
        onConfirm={confirmDeleteAthlete}
        cancelButtonText="Cancel"
        showWarning={true}
        warningText={
          athleteToDelete?.memberType === 'Visitor'
            ? 'This action cannot be undone. The athlete will be unregistered from this meet, and — since visitors only exist for their registrations — removed from your club entirely if they have no other registrations.'
            : 'This action cannot be undone. The athlete will be permanently unregistered from this meet.'
        }
        isLoading={isDeleting}
      />
    </AdminTablePageLayout>
  );
};

MeetRegistrationEdit.propTypes = {
  clubId: PropTypes.string.isRequired,
  meetId: PropTypes.string.isRequired,
};

export default MeetRegistrationEdit;
